import {inject, Injectable} from '@angular/core';
import {catchError, map, Observable, of} from 'rxjs';
import {
  ProjectFixedCollectionControllerService,
  ProjectFixedCollectionData,
  ProjectFixedCollectionRequest,
  ProjectFixedCollectionResponse,
  ProjectFixedMultipleCollectionsRequest,
} from '@frontend/shared-api';

@Injectable({
  providedIn: 'root',
})
export class ProjectFixedCollectionService {
  private readonly projectFixedController = inject(ProjectFixedCollectionControllerService);

  getDirectusIdsForCollection(projectId: number, collectionName: string): Observable<string[]> {
    return this.projectFixedController
      .getValuesForProjectCollection({projectId, collectionName})
      .pipe(
        map((response) => response.directusIds),
        catchError((error) => {
          console.error(
            `Error getting directus IDs for project ${projectId}, collection ${collectionName}:`,
            error,
          );
          return of([]);
        }),
      );
  }

  getValuesWithCollectionId(
    projectId: number,
    collectionName: string,
  ): Observable<{collectionId: number; directusIds: string[]}> {
    return this.projectFixedController
      .getValuesForProjectCollection({projectId, collectionName})
      .pipe(
        map((response) => ({
          collectionId: response.collectionId,
          directusIds: response.directusIds,
        })),
        catchError((error) => {
          console.error(
            `Error getting values with collection ID for project ${projectId}, collection ${collectionName}:`,
            error,
          );
          return of({collectionId: 0, directusIds: []});
        }),
      );
  }

  getDirectusIdsForCollectionsBatch(
    projectId: number,
    collectionNames: string[],
  ): Observable<{[key: string]: string[]}> {
    if (collectionNames.length === 0) {
      return of({});
    }

    return this.projectFixedController
      .getValuesForProjectCollections({projectId, collectionNames})
      .pipe(
        map((response) => response.collections),
        catchError((error) => {
          console.error(
            `Error getting directus IDs for project ${projectId}, collections ${collectionNames.join(', ')}:`,
            error,
          );
          return of({});
        }),
      );
  }

  createMultiple(
    projectId: number,
    collections: ProjectFixedCollectionData[],
  ): Observable<{[key: string]: string[]}> {
    if (collections.length === 0) {
      return of({});
    }

    const dto: ProjectFixedMultipleCollectionsRequest = {
      projectId,
      collections,
    };

    return this.projectFixedController
      .createMultipleCollections({projectId, projectFixedMultipleCollectionsRequest: dto})
      .pipe(
        map((response) => response.collections),
        catchError((error) => {
          console.error(
            `Error creating multiple fixed collections for project ${projectId}:`,
            error,
          );
          return of({});
        }),
      );
  }

  create(dto: ProjectFixedCollectionRequest): Observable<ProjectFixedCollectionResponse | null> {
    const projectId = dto.projectId;
    return this.projectFixedController
      .createCollection({projectId, projectFixedCollectionRequest: dto})
      .pipe(
        catchError((error) => {
          console.error(
            `Error creating fixed values for project ${dto.projectId}, collection ${dto.name}:`,
            error,
          );
          return of(null);
        }),
      );
  }
}
